import { Component, inject, OnInit } from '@angular/core';
import Swal from 'sweetalert2';
import { DataTable, CellTemplateDirective } from '@components/data-table/data-table';
import { ButtonActions } from '@components/button-actions/button-actions';
import { PageHeader } from '@components/page-header/page-header';
import { ProspectModal } from '@components/prospect-modal/prospect-modal';
import { ProspectHistoryModal } from '@components/prospect-history-modal/prospect-history-modal';
import { ProspectDocumentsModal } from '@components/prospect-documents-modal/prospect-documents-modal';
import { RescheduleModal, type RescheduleData, type ScheduleModalMode } from '@components/reschedule-modal/reschedule-modal';
import { AcceptancePreviewModal } from '@components/acceptance-preview-modal/acceptance-preview-modal';
import { ProspectService } from '@services/prospect-service';
import { StageColorService } from '@services/stage-color-service';
import { NotifyService } from '@intelishs-group/notify-service';
import type { ColumnDef, TableAction } from '@components/data-table/data-table.types';
import type { ActionButtonDef } from '@components/button-actions/button-actions.types';
import { actionPreset } from '@components/button-actions/button-actions.types';
import type { VEtapaProspectoDTO, Prospect } from '@schemas/prospect';
import { toLocalISOString } from '@shared/utils/date.utils';
import { STAGE_ACTIONS, RECHAZO_ACTION, ACTION_ADVANCE_MAP, PROSPECT_ACTIONS } from '@shared/constants/prospect-actions.constants';

@Component({
  selector: 'app-prospectos',
  templateUrl: './prospectos.page.html',
  styleUrl: './prospectos.page.css',
  standalone: true,
  imports: [DataTable, CellTemplateDirective, ButtonActions, PageHeader, ProspectModal, ProspectHistoryModal, ProspectDocumentsModal, RescheduleModal, AcceptancePreviewModal],
})
export class Prospectos implements OnInit {
  private prospectService = inject(ProspectService);
  private stageColorService = inject(StageColorService);
  private notifyService = inject(NotifyService);

  data: VEtapaProspectoDTO[] = [];
  allData: VEtapaProspectoDTO[] = [];
  activeStageFilter: string | null = null;
  stageList: { name: string; color: string }[] = [];
  isLoading = false;
  modalVisible = false;
  historyModalVisible = false;
  documentsModalVisible = false;
  rescheduleModalVisible = false;
  previewModalVisible = false;
  scheduleMode: ScheduleModalMode = 'reagendar';
  selectedProspect: VEtapaProspectoDTO | null = null;
  private stageActions = STAGE_ACTIONS;
  private rechazoAction = RECHAZO_ACTION;
  private actionAdvanceMap = ACTION_ADVANCE_MAP;
  private documentosAction = PROSPECT_ACTIONS['documentos'];
  private previewAction = PROSPECT_ACTIONS['vista-previa-formato'];

  // Cache de acciones por paso para evitar recalcular en cada ciclo de change detection
  private actionsCache = new Map<number, ActionButtonDef[]>();

  getActionsForRow(row: VEtapaProspectoDTO): ActionButtonDef[] {
    const paso = row.fkIdPaso ?? 0;
    if (this.actionsCache.has(paso)) return this.actionsCache.get(paso)!;
    const isReadonly = paso === 31 || paso === 21;
    const stageActions = this.stageActions[paso] ?? [];
    const actions = [
      actionPreset('ver'),
      ...(isReadonly ? [] : [actionPreset('editar'), this.rechazoAction]),
      ...stageActions,
      ...(!isReadonly && paso >= 4 ? [this.documentosAction] : []),
      ...(paso === 21 ? [this.previewAction] : []),
    ];
    this.actionsCache.set(paso, actions);
    return actions;
  }

  columns: ColumnDef<VEtapaProspectoDTO>[] = [
    // { key: 'bactivo', header: '', minWidth: '30px', align: 'center', type: 'status' },
    { key: 'setapa', header: 'Etapa', minWidth: '80px', align: 'center', type: 'tag', filterable: true, sortable: true,
      tagColorFn: (v) => this.stageColorService.getByMainName(String(v ?? '')),
    },
    { key: 'fkIdProspecto', header: 'ID', minWidth: '40px', align: 'center', sortable: true },
    // { key: 'ffechaRegistro', header: 'Fecha Registro', minWidth: '40px', align: 'center', type: 'date', sortable: true },
    { key: 'sprospecto', header: 'Prospecto', minWidth: '300px', filterable: true, sortable: true },
    { key: 'spaso', header: 'Paso', minWidth: '250px', align: 'center', filterable: true },
    { key: 'acciones', header: 'Acciones', minWidth: '150px', align: 'center', type: 'custom' },
  ];

  async ngOnInit() {
    await this.stageColorService.ensureLoaded();
    this.stageList = this.stageColorService.getMainStageList();
    await this.loadProspects();
  }

  async loadProspects() {
    this.isLoading = true;
    try {
      const result = await this.prospectService.prospects();
      this.allData = result ?? [];
    } catch (error) {
      const msg = error instanceof Error ? error.message : typeof error === 'string' ? error : 'Error al cargar prospectos';
      this.notifyService.errorNotyfy(msg);
      this.allData = [];
    } finally {
      this.applyStageFilter();
      this.isLoading = false;
    }
  }

  getStageCount(stageName: string): number {
    return this.allData.filter(
      d => d.setapa?.toLowerCase() === stageName.toLowerCase()
    ).length;
  }

  toggleStageFilter(stageName: string): void {
    if (this.activeStageFilter === stageName) {
      this.activeStageFilter = null;
    } else {
      this.activeStageFilter = stageName;
    }
    this.applyStageFilter();
  }

  clearStageFilter(): void {
    this.activeStageFilter = null;
    this.applyStageFilter();
  }

  private applyStageFilter(): void {
    if (this.activeStageFilter) {
      const filter = this.activeStageFilter.toLowerCase();
      this.data = this.allData.filter(
        d => d.setapa?.toLowerCase() === filter
      );
    } else {
      this.data = [...this.allData];
    }
  }

  onAdd() {
    this.selectedProspect = null;
    this.modalVisible = true;
  }


  onAction(event: TableAction<VEtapaProspectoDTO>) {
    switch (event.action) {
      case 'ver':
        this.selectedProspect = event.row;
        this.historyModalVisible = true;
        break;
      case 'editar':
        this.selectedProspect = event.row;
        this.modalVisible = true;
        break;
      case 'documentos':
        this.selectedProspect = event.row;
        this.documentsModalVisible = true;
        break;
      case 'reagendar':
      case 'reagenda-propuesta':
        this.selectedProspect = event.row;
        this.scheduleMode = 'reagendar';
        this.rescheduleModalVisible = true;
        break;
      case 'sesion-propuesta':
        this.selectedProspect = event.row;
        this.scheduleMode = 'sesion-propuesta';
        this.rescheduleModalVisible = true;
        break;
      case 'aceptacion':
        this.handleAceptacion(event.row);
        break;
      case 'vista-previa-formato':
        this.selectedProspect = event.row;
        this.previewModalVisible = true;
        break;
      case 'rechazo':
        this.confirmRechazo(event.row);
        break;
      default:
        this.advanceStage(event.row, event.action);
        break;
    }
  }

  async confirmRechazo(row: VEtapaProspectoDTO) {
    const result = await Swal.fire({
      title: '¿Estás seguro de rechazar este prospecto?',
      text: 'Esta acción moverá el prospecto a la etapa de Rechazo',
      icon: 'warning',
      input: 'textarea',
      inputLabel: 'Motivo de rechazo',
      inputPlaceholder: 'Escribe el motivo del rechazo...',
      inputValidator: (value) => {
        if (!value?.trim()) return 'El motivo es obligatorio';
        return null;
      },
      showCancelButton: true,
      confirmButtonText: 'Rechazar',
      cancelButtonText: 'Cancelar',
      allowOutsideClick: false,
      allowEscapeKey: false,
      buttonsStyling: false,
      customClass: {
        confirmButton: 'confirm-btnSweet',
        cancelButton: 'cancel-btnSweet',
        actions: 'swal-actions-spaced',
      },
    });
    if (result.isConfirmed) {
      await this.advanceStage(row, 'rechazo', result.value);
    }
  }

  async advanceStage(row: VEtapaProspectoDTO, action: string, smotivo?: string) {
    const idDestinationStage = this.actionAdvanceMap[action];
    if (!idDestinationStage || !row.fkIdProspecto) return;

    try {
      await this.prospectService.stage_Advance({
        idProspect: row.fkIdProspecto,
        idDestinationStage,
        ...(smotivo ? { smotivo } : {}),
      });
      this.notifyService.successNotyfy('Avance exitoso', 'El prospecto ha sido actualizado');
      await this.loadProspects();
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Error al avanzar el prospecto';
      this.notifyService.errorNotyfy(msg);
    }
  }

  async onScheduled(data: RescheduleData) {
    if (!this.selectedProspect?.fkIdProspecto) return;

    const fecha = new Date(data.fecha);
    const hora = new Date(data.hora);
    fecha.setHours(hora.getHours(), hora.getMinutes(), 0, 0);

    if (this.scheduleMode === 'sesion-propuesta') {
      try {
        await this.prospectService.agenda_Reagendar({
          scomentarioCierre: data.scomentario,
          fkidprospecto: this.selectedProspect.fkIdProspecto,
          scomentario: data.scomentario,
          ffechaagenda: toLocalISOString(fecha),
          sgoogleEventId: data.sgoogleEventId,
        });
        this.notifyService.successNotyfy('Agenda creada', 'La sesión de propuesta ha sido agendada correctamente');
        await this.loadProspects();
      } catch (error) {
        const msg = error instanceof Error ? error.message : 'Error al crear la agenda';
        this.notifyService.errorNotyfy(msg);
      }
    } else {
      const pasosPermitidos = [2, 3, 14, 15];
      if (!pasosPermitidos.includes(this.selectedProspect.fkIdPaso ?? 0)) {
        this.notifyService.errorNotyfy('No se puede reagendar desde este paso');
        return;
      }
      
      try {
        await this.prospectService.agenda_Reagendar({
          scomentarioCierre: data.scomentario,
          fkidprospecto: this.selectedProspect.fkIdProspecto,
          scomentario: data.scomentario,
          ffechaagenda: toLocalISOString(fecha),
          sgoogleEventId: data.sgoogleEventId,
        });
        this.notifyService.successNotyfy('Reagendado', 'La cita ha sido reagendada correctamente');
        await this.loadProspects();
      } catch (error) {
        const msg = error instanceof Error ? error.message : 'Error al reagendar el prospecto';
        this.notifyService.errorNotyfy(msg);
      }
    }
  }

  async onDocumentUploaded(isFirstUpload: boolean) {
    if (!this.selectedProspect?.fkIdProspecto) return;

    const currentStep = this.selectedProspect.fkIdPaso ?? 0;
    if (!isFirstUpload || currentStep !== 4) {
      await this.loadProspects();
      return;
    }

    try {
      await this.prospectService.stage_Advance({
        idProspect: this.selectedProspect.fkIdProspecto,
        idDestinationStage: 11,
      });
      this.notifyService.successNotyfy('Avance exitoso', 'Prospecto avanzado a Análisis jurídico');
      await this.loadProspects();
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Error al avanzar el prospecto';
      this.notifyService.errorNotyfy(msg);
    }
  }

  async handleAceptacion(row: VEtapaProspectoDTO): Promise<void> {
    if (!row.fkIdProspecto) return;
    this.isLoading = true;
    try {
      const prospect = await this.prospectService.byId(row.fkIdProspecto);
      const missing = this.validateProspectComplete(prospect);
      if (missing.length > 0) {
        this.notifyService.errorNotyfy(`Faltan datos del prospecto: ${missing.join(', ')}`);
        return;
      }
      await this.prospectService.stage_Advance({
        idProspect: row.fkIdProspecto,
        idDestinationStage: 21,
      });
      this.notifyService.successNotyfy('Avance exitoso', 'El prospecto fue aceptado');
      await this.loadProspects();
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Error al avanzar el prospecto';
      this.notifyService.errorNotyfy(msg);
    } finally {
      this.isLoading = false;
    }
  }

  private validateProspectComplete(p: Prospect | null | undefined): string[] {
    const missing: string[] = [];
    const isEmpty = (v: unknown) => v == null || (typeof v === 'string' && v.trim() === '');
    if (!p) { missing.push('Prospecto no encontrado'); return missing; }

    if (isEmpty(p.nombre)) missing.push('Nombre');
    if (isEmpty(p.rfc)) missing.push('RFC');
    if (isEmpty(p.email)) missing.push('Email');

    const info = p.datosJson?.prospecto;
    if (isEmpty(info?.snombrecomercial)) missing.push('Nombre Comercial');
    if (isEmpty(info?.sactividad)) missing.push('Actividad');
    if (info?.iempleados == null) missing.push('Empleados');
    if (isEmpty(info?.scodigopostal)) missing.push('Código Postal');
    if (isEmpty(info?.sdireccion)) missing.push('Dirección');
    if (isEmpty(info?.snumeroexterior)) missing.push('Número Exterior');
    if (isEmpty(info?.scolonia)) missing.push('Colonia');
    if (isEmpty(info?.slocalidad)) missing.push('Estado');
    if (isEmpty(info?.smunicipio)) missing.push('Municipio');
    if (!info?.regimenesfiscales?.length) missing.push('Regímenes Fiscales');
    if (p.rel_Prospect_Broker?.idBroker == null) missing.push('Broker');

    const c = p.datosJson?.contacto;
    if (isEmpty(c?.snombre)) missing.push('Nombre Contacto');
    if (isEmpty(c?.itelefono)) missing.push('Teléfono');
    if (isEmpty(c?.scorreo)) missing.push('Correo Contacto');

    return missing;
  }

  async onModalSaved() {
    await this.loadProspects();
  }
}
